/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { ResponsiveTable, useLocalization } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { MembershipProgramWithStats } from "@lemmonhq/types";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { useTrpc } from "@app/vue/providers/index";
import { MembershipProgramEditor } from "./MembershipProgramEditor";
import { MembershipMembersSheet } from "./MembershipMembersSheet";

export type MembershipProgramsPageProps = {
  scope?: "shop" | "group";
  canManage?: boolean;
};

export const MembershipProgramsPage =
  defineComponent<MembershipProgramsPageProps>({
    name: "MembershipProgramsPage",
    props: ["scope", "canManage"],
    setup(props) {
      const { t } = useLocalization();
      const trpc = useTrpc();
      const queryClient = useQueryClient();
      const editing = ref<MembershipProgramWithStats | null>(null);
      const editorOpen = ref(false);
      const managingMembers = ref<MembershipProgramWithStats | null>(null);

      const query = useQuery({
        queryKey: ["membershipPrograms", props.scope ?? "shop", "list"],
        queryFn: () =>
          props.scope === "group"
            ? trpc.membershipPrograms.groupList.query({ enabled_only: false })
            : trpc.membershipPrograms.list.query({ enabled_only: false }),
      });
      const programs = computed(
        () => (query.data.value ?? []) as MembershipProgramWithStats[],
      );

      const toggleMutation = useMutation({
        mutationFn: (input: { id: string; enabled: boolean }) =>
          props.scope === "group"
            ? trpc.membershipPrograms.groupToggleEnabled.mutate(input)
            : trpc.membershipPrograms.toggleEnabled.mutate(input),
        onSuccess: () =>
          queryClient.invalidateQueries({ queryKey: ["membershipPrograms"] }),
      });

      const open = (program?: MembershipProgramWithStats) => {
        editing.value = program ?? null;
        editorOpen.value = true;
      };
      const close = () => {
        editing.value = null;
        editorOpen.value = false;
      };

      const columns = computed<ResponsiveTableColumn[]>(() => [
        {
          key: "name",
          label: t("group.loyalty.program_name"),
          mobilePriority: "primary",
          render: (row: MembershipProgramWithStats) => (
            <div class="flex items-center gap-3">
              <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <span class="material-symbols-outlined">card_membership</span>
              </div>
              <div>
                <div class="font-medium text-accent">{row.name}</div>
                <div class="text-xs text-base-content/50">
                  {row.description}
                </div>
              </div>
            </div>
          ),
        },
        {
          key: "members",
          label: t("group.loyalty.members"),
          mobilePriority: "secondary",
          render: (row: MembershipProgramWithStats) => (
            <button
              class="btn btn-ghost btn-xs gap-1"
              onClick={(event) => {
                event.stopPropagation();
                managingMembers.value = row;
              }}
            >
              <span class="material-symbols-outlined text-base">group</span>
              {row.active_members}
            </button>
          ),
        },
        {
          key: "limit",
          label: t("group.loyalty.usage_limit"),
          mobilePriority: "secondary",
          render: (row: MembershipProgramWithStats) =>
            row.cap_period
              ? t("group.loyalty.usage_limit_value", [
                  row.cap_count ?? 0,
                  row.cap_period,
                ])
              : t("group.loyalty.unlimited"),
        },
        {
          key: "actions",
          label: "",
          mobilePriority: "actions",
          render: (row: MembershipProgramWithStats) => (
            <div
              class="flex justify-end gap-2"
              onClick={(event) => event.stopPropagation()}
            >
              <input
                type="checkbox"
                class="toggle toggle-primary toggle-sm"
                checked={row.enabled}
                disabled={
                  toggleMutation.isPending.value || props.canManage === false
                }
                onChange={(event) =>
                  toggleMutation.mutate({
                    id: row.id,
                    enabled: (event.target as HTMLInputElement).checked,
                  })
                }
              />
              {props.canManage !== false && (
                <button class="btn btn-ghost btn-xs" onClick={() => open(row)}>
                  <span class="material-symbols-outlined text-base">edit</span>
                </button>
              )}
            </div>
          ),
        },
      ]);

      return () => (
        <AppSubpage>
          <AppSubpageHeader
            icon="card_membership"
            title={
              props.scope === "group"
                ? t("group.loyalty.memberships_title")
                : t("promotions.memberships.page_title")
            }
            description={
              props.scope === "group"
                ? t("group.loyalty.memberships_description")
                : t("promotions.memberships.page_description")
            }
            v-slots={{
              actions: () =>
                props.canManage !== false ? (
                  <button
                    class="btn btn-primary btn-sm gap-2"
                    onClick={() => open()}
                  >
                    <span class="material-symbols-outlined text-lg">add</span>
                    {t("group.loyalty.new_program")}
                  </button>
                ) : null,
            }}
          />
          <AppSubpageContent>
            <div class="p-4">
              <ResponsiveTable
                columns={columns.value}
                rows={programs.value}
                rowKey={(row: MembershipProgramWithStats) => row.id}
                onRowClick={(row: MembershipProgramWithStats) => {
                  if (props.canManage !== false) open(row);
                }}
                loading={query.isLoading.value}
                emptyMessage={t("group.loyalty.no_programs")}
              />
            </div>
          </AppSubpageContent>
          {editorOpen.value && props.canManage !== false && (
            <MembershipProgramEditor
              program={editing.value ?? undefined}
              scope={props.scope}
              onClose={close}
              onSaved={close}
            />
          )}
          {managingMembers.value && (
            <MembershipMembersSheet
              program={managingMembers.value}
              scope={props.scope}
              canManage={props.canManage}
              onClose={() => (managingMembers.value = null)}
            />
          )}
        </AppSubpage>
      );
    },
  });
